import React, { useEffect, useMemo, useState } from 'react';

import { asNumber, guessType } from '@rjsf/utils';
import { FormGroup, Input } from 'src/components/ui';
import Select from 'react-select';
import useWrapInput from './utils/useWrapInput';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import { useJnx } from '../../util/jnx';
import { useDebouncedEffect } from '../../hooks/useDebounceEffect';
import { debounce } from 'lodash';
import useTestId from '../../hooks/useTestId';
import { useAutofillSafeName } from '../../hooks/useAutofillSafeName';
import getPathFromId from '../../util/getPathFromId';
import classnames from '../../util/classnames';

const nums = new Set(['number', 'integer']);

function processValue(schema, value) {
  const { type, items } = schema;
  if (value === '') return undefined;
  if (type === 'array' && items && nums.has(items.type)) return value.map(asNumber);
  if (type === 'boolean') return value === 'true';
  if (type === 'number') return asNumber(value);
  if (schema.enum) {
    if (schema.enum.every(x => guessType(x) === 'number')) return asNumber(value);
    if (schema.enum.every(x => guessType(x) === 'boolean')) return value === 'true';
  }
  return value;
}

function getValue(event, multiple) {
  if (multiple) {
    return Array.from(event.target.options)
      .filter(o => o.selected)
      .map(o => o.value);
  } else {
    return event.target.value;
  }
}

function SelectWidget(props) {
  const {
    schema,
    id,
    options,
    value,
    required,
    disabled,
    readonly,
    multiple,
    autofocus = false,
    onChange,
    onBlur,
    onFocus,
    placeholder,
    registry,
    uiSchema: {
      'ui:allowSelectNone': allowSelectNone = false,
      'ui:classNames': classNames = '',
      'ui:prefix': prefix = '',
      'ui:sufix': sufix = '',
      'ui:tooltipText': tooltipText = '',
      'ui:onChange': onChangeExpr,
      'ui:staticText': staticText = false,
    },
  } = props;
  const formContext = registry?.formContext;
  const { enumOptions, enumDisabled } = options;
  const emptyValue = multiple ? [] : '';

  const { setFormDataValues } = formContext;
  const functionBinds = useMemo(() => ({ set: setFormDataValues }), [setFormDataValues]);
  const rootFormData = useSideChannelSubscription(formContext.sideChannel, 0);
  const dataPath = useMemo(() => getPathFromId(id), [id]);

  const [current, setCurrent] = useState(value);
  const testIdProps = useTestId(id);
  const autofillSafeName = useAutofillSafeName();

  useEffect(() => {
    if (value !== current) {
      setCurrent(value);
    }
  }, [value]);

  const onChangeJnx = useJnx(onChangeExpr, { functionBinds });

  const handleChange = debounce(
    newValue => {
      if (newValue !== value) {
        onChange(newValue);
      }

      if (onChangeExpr) {
        setTimeout(
          () =>
            onChangeJnx.evalAsync(onChangeExpr, '', {
              rootFormData,
              newValue,
            }),
          100
        );
      }
    },
    300,
    { trailing: true }
  );

  const displayOptions = useMemo(() => {
    const enumNames = schema.items?.enumNames;
    if (!enumNames) return enumOptions;

    return (enumOptions || []).map((opt, i) => ({
      ...opt,
      label: enumNames[i] || opt.label,
    }));
  }, [enumOptions, schema.items?.enumNames]);

  const selectValue = useMemo(() => {
    if (!Array.isArray(current)) return [];
    const set = new Set((current || []).map(String));
    return (displayOptions || []).filter(opt => set.has(String(opt.value)));
  }, [current, displayOptions]);

  const staticTextValue = useMemo(() => {
    if (multiple) return selectValue.map(opt => opt.label).join(', ');
    const opt = (displayOptions || []).find(o => String(o.value) === String(current));
    return opt ? opt.label : '';
  }, [multiple, selectValue, displayOptions, current]);

  const multiInput = (
    <Select
      inputId={id}
      {...testIdProps}
      className="autocomplete-container"
      classNamePrefix="autocomplete"
      isMulti
      isDisabled={disabled || readonly}
      autoFocus={autofocus}
      options={displayOptions || []}
      value={selectValue}
      placeholder={placeholder || '---'}
      menuPortalTarget={document.body}
      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
      closeMenuOnSelect={false}
      isOptionDisabled={opt => !!(enumDisabled && enumDisabled.includes(opt.value))}
      onChange={selected => {
        const newValues = (selected || []).map(opt => opt.value);
        const processed = processValue(schema, newValues);
        handleChange(processed);
        setCurrent(processed);
      }}
      onBlur={onBlur ? () => onBlur(id, processValue(schema, current || [])) : undefined}
      onFocus={onFocus ? () => onFocus(id, processValue(schema, current || [])) : undefined}
    />
  );

  const singleInput = (
    <Input
      id={id}
      {...testIdProps}
      type="select"
      name={autofillSafeName}
      autoComplete="off"
      className={`form-control ${classNames}`}
      value={typeof current === 'undefined' ? emptyValue : current}
      required={required}
      disabled={disabled || readonly}
      autoFocus={autofocus}
      onBlur={
        onBlur &&
        (event => {
          const newValue = getValue(event, false);
          onBlur(id, processValue(schema, newValue));
        })
      }
      onFocus={
        onFocus &&
        (event => {
          const newValue = getValue(event, false);
          onFocus(id, processValue(schema, newValue));
        })
      }
      onChange={event => {
        const newValue = processValue(schema, getValue(event, false));
        handleChange(newValue);
        setCurrent(newValue);
      }}
    >
      {schema.default === undefined && (
        <option disabled={!allowSelectNone} value="">
          {placeholder || '---'}
        </option>
      )}
      {(displayOptions || []).map(({ value, label }, i) => {
        const disabled = enumDisabled && enumDisabled.indexOf(value) !== -1;

        return (
          <option key={i} value={value} disabled={disabled}>
            {label}
          </option>
        );
      })}
    </Input>
  );

  return (
    <FormGroup className={classnames({ 'input-wrapper': !staticText })}>
      {staticText ? (
        <div className={classNames}>{staticTextValue}</div>
      ) : (
        useWrapInput({
          input: multiple ? multiInput : singleInput,
          prefix,
          sufix,
          tooltipText,
          rootFormData,
          dataPath,
          fieldId: id,
          formContext,
          formData: value,
          fieldSchema: schema,
        })
      )}
    </FormGroup>
  );
}

export default SelectWidget;
